import React, { useState } from "react";
import { apiClient } from "@finitless/shared/lib";
import { useAuth } from "@finitless/shared/contexts";
import { Icon } from "../../";
import { IntegrationLogs, PluginModal, SyncButtons } from "../";
import { usePluginEnvironment } from "../../../../hooks";

const IntegrationActions = ({
  integration,
  business,
  parseTemplate,
  onSetupClick,
  onDisconnect,
  isDisconnecting,
  onConfigComplete,
}) => {
  const [showLogs, setShowLogs] = useState(false);
  const [pluginModal, setPluginModal] = useState({
    open: false,
    title: "",
    url: "",
  });
  const { currentProject } = useAuth();
  const token = apiClient.getAuthToken();
  const {
    isSandbox,
    isLoading: isEnvironmentLoading,
    toggleEnvironment,
    isToggling,
  } = usePluginEnvironment(integration?.plugin?.root);

  const handleOpenPluginDetails = (e, plugin, linkKey) => {
    e.stopPropagation();

    if (!plugin?.links?.[linkKey] || !parseTemplate) return;

    // Parse template variables with actual project data
    const variables = {
      project_code: currentProject || "",
      access_token: token || "",
      business_id: business?.id,
    };

    // If clicking on general_settings, open setup_url instead
    let targetUrl = plugin?.links[linkKey];
    if (linkKey === "general_settings" && plugin?.setup_url) {
      targetUrl = plugin.setup_url;
    }

    const url = parseTemplate(plugin?.root + targetUrl, variables);

    setPluginModal({
      open: true,
      title: plugin.name,
      url: url,
    });
  };

  const handleOpenLogs = () => {
    setShowLogs(true);
  };

  const handleCloseLogs = () => {
    setShowLogs(false);
  };

  const handleClosePluginModal = () => {
    setPluginModal({ open: false, title: "", url: "" });
    onConfigComplete && onConfigComplete();
  };

  const capitalize = (str) => {
    return str.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (!integration?.plugin?.links) return null;

  return (
    <>
      {/* Settings Section */}
      <div className="space-y-3">
        <div className="text-center">
          <p className="mb-3 text-xs text-gray-400">Settings</p>
          <div className="flex flex-wrap justify-center gap-2">
            {/* POS Setup button */}
            {onSetupClick && (
              <button
                onClick={onSetupClick}
                className="inline-flex w-full items-center justify-center rounded-lg border border-blue-500/30 bg-blue-900/20 px-4 py-2 text-sm font-medium text-blue-400 transition-colors hover:border-blue-400/50 hover:bg-blue-900/30"
              >
                <Icon name="Settings" className="mr-2 h-4 w-4" />
                Setup
              </button>
            )}

            {/* Dynamic buttons from plugin.links */}
            {Object.keys(integration.plugin.links).map((key) => (
              <button
                key={key}
                onClick={(e) =>
                  handleOpenPluginDetails(e, integration.plugin, key)
                }
                className="inline-flex w-full items-center justify-center rounded-lg border border-blue-500/30 bg-blue-900/20 px-4 py-2 text-sm font-medium text-blue-400 transition-colors hover:border-blue-400/50 hover:bg-blue-900/30"
              >
                <Icon name="ExternalLink" className="mr-2 h-4 w-4" />
                {key === "general_settings" ? "Settings" : capitalize(key)}
              </button>
            ))}

            {/* Sandbox Mode Toggle */}
            {integration?.plugin?.root && (
              <button
                onClick={toggleEnvironment}
                disabled={isToggling || isEnvironmentLoading}
                className={`inline-flex w-full items-center justify-center rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  isSandbox
                    ? "border-yellow-500/30 bg-yellow-900/20 text-yellow-400 hover:border-yellow-400/50 hover:bg-yellow-900/30"
                    : "border-blue-500/30 bg-blue-900/20 text-blue-400 hover:border-blue-400/50 hover:bg-blue-900/30"
                }`}
              >
                {isToggling ? (
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-current"></div>
                ) : (
                  <Icon
                    name={isSandbox ? "ToggleRight" : "ToggleLeft"}
                    className="mr-2 h-4 w-4"
                  />
                )}
                {isEnvironmentLoading
                  ? "Loading..."
                  : isSandbox
                    ? "Sandbox Mode (ON)"
                    : "Sandbox Mode (OFF)"}
              </button>
            )}
          </div>
        </div>

        {/* Action Section */}
        <div className="border-t border-gray-700 pt-3 text-center">
          <p className="mb-3 text-xs text-gray-400">Action</p>
          <SyncButtons
            businessId={business?.id}
            integrationId={integration?.id}
          />
        </div>

        {/* Additional Options Section */}
        <div className="border-t border-gray-700 pt-3 text-center">
          <p className="mb-3 text-xs text-gray-400">Additional Options</p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              onClick={handleOpenLogs}
              className="inline-flex w-full items-center justify-center rounded-lg border border-blue-500/30 bg-blue-900/20 px-4 py-2 text-sm font-medium text-blue-400 transition-colors hover:border-blue-400/50 hover:bg-blue-900/30"
            >
              <Icon name="FileText" className="mr-2 h-4 w-4" />
              View Logs
            </button>

            {/* Disconnect button */}
            {onDisconnect && (
              <button
                onClick={onDisconnect}
                disabled={isDisconnecting}
                className="inline-flex w-full items-center justify-center rounded-lg border border-red-500/30 bg-red-900/20 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:border-red-400/50 hover:bg-red-900/30 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isDisconnecting ? (
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-b-2 border-red-400"></div>
                ) : (
                  <Icon name="X" className="mr-2 h-4 w-4" />
                )}
                Disconnect POS
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Integration Logs Modal */}
      <IntegrationLogs
        isOpen={showLogs}
        onClose={handleCloseLogs}
        model="Business"
        modelId={business?.id}
        pluginId={integration?.plugin_id}
      />

      {/* Plugin Modal */}
      <PluginModal
        isOpen={pluginModal.open}
        onClose={handleClosePluginModal}
        title={pluginModal.title}
        url={pluginModal.url}
      />
    </>
  );
};

export default IntegrationActions;
